import {
  type PresetEditing,
  VERSION_NUMBER_LENGTH,
  VersionNumber,
} from "@pax/api-contract/presets/v1alpha1";
import { solidSurface } from "@pax/ui";
import {
  type ClipboardEvent,
  createContext,
  type FormEvent,
  type ReactNode,
  useContext,
  useState,
} from "react";
import { api, requestErrorMessage } from "@/lib/api";
import { ScreenOverlay } from "@/shell/Screen";
import { primaryButton, secondaryButton } from "./parts";

/**
 * What's wrong with a typed version number, or null when it will do. While it's still being typed,
 * a number one more digit would make right, such as 1., isn't wrong yet.
 */
export function versionNumberProblem(typed: string, finished = false): string | null {
  const parsed = VersionNumber.safeParse(typed);
  if (parsed.success) return null;
  if (!finished && VersionNumber.safeParse(`${typed.trim()}0`).success) return null;
  return parsed.error.issues[0]?.message ?? null;
}

/**
 * Keeps a field to so many characters, and notices when something would push it past them, typed
 * or pasted, so it can say why nothing happened. Spread `field` onto the input.
 */
export function useCharacterLimit(limit: number) {
  const [tried, setTried] = useState(false);
  /** Whether adding this text where the cursor is would run past the limit. */
  const wouldOverflow = (input: HTMLInputElement | HTMLTextAreaElement, added: string) => {
    const replaced = (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
    return input.value.length - replaced + added.length > limit;
  };
  return {
    tooLong: tried ? `${limit} characters max` : null,
    reset: () => setTried(false),
    field: {
      maxLength: limit,
      onBlur: () => setTried(false),
      onBeforeInput: (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const added = (event as unknown as { data?: string | null }).data ?? "";
        setTried(added.length > 0 && wouldOverflow(event.currentTarget, added));
      },
      onPaste: (event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setTried(wouldOverflow(event.currentTarget, event.clipboardData.getData("text")));
      },
    },
  };
}

/**
 * Asks for the number a new version goes by before starting it. Numbers are digits and periods,
 * at most five characters, and no two versions of a preset share one.
 */
function NewVersionDialog({
  basedOn,
  taken,
  onCreate,
  onClose,
}: {
  /** The number of the version it starts from, such as v5, or null for one started blank. */
  basedOn: string | null;
  /** Every number this preset's versions already go by. */
  taken: readonly string[];
  onCreate: (versionNumber: string) => Promise<void>;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** Whether Create was pressed since the number last changed, so unfinished numbers say why. */
  const [submitted, setSubmitted] = useState(false);
  const numberLimit = useCharacterLimit(VERSION_NUMBER_LENGTH);
  const parsed = VersionNumber.safeParse(typed);
  const clash = parsed.success && taken.includes(parsed.data);
  const shown =
    numberLimit.tooLong ??
    problem ??
    versionNumberProblem(typed, submitted) ??
    (clash ? `Already used v${typed.trim()}` : null);

  const create = async () => {
    setSubmitted(true);
    if (!parsed.success || clash) return;
    setCreating(true);
    setProblem(null);
    try {
      await onCreate(parsed.data);
      onClose();
    } catch (error) {
      setProblem(requestErrorMessage(error));
      setCreating(false);
    }
  };

  return (
    <ScreenOverlay>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onClose}
        className="overlay-scrim absolute inset-0 cursor-default"
      />
      <form
        role="dialog"
        aria-label="New draft version"
        onSubmit={event => {
          event.preventDefault();
          void create();
        }}
        onKeyDown={event => {
          if (event.key === "Escape") onClose();
        }}
        className={`overlay-panel relative w-full max-w-xs p-5 ${solidSurface}`}
      >
        <p className="text-base font-medium">New draft version</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {basedOn ? `Based on ${basedOn}` : "Started from scratch"}
        </p>
        <label className="mt-4 block text-sm font-medium" htmlFor="new-version-number">
          Version number
        </label>
        <span className="mt-1.5 flex h-10 items-center rounded-xl bg-foreground/5 px-3 text-sm tabular-nums focus-within:bg-foreground/[0.07]">
          <span className="text-muted-foreground">v</span>
          <input
            id="new-version-number"
            value={typed}
            inputMode="decimal"
            placeholder="1.2"
            disabled={creating}
            ref={input => input?.focus()}
            onChange={event => {
              setTyped(event.target.value);
              setProblem(null);
              setSubmitted(false);
            }}
            className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground/50 disabled:opacity-60"
          />
        </span>
        <p className={`mt-1.5 min-h-4 text-xs ${shown ? "text-destructive" : ""}`}>{shown}</p>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={secondaryButton}>
            Cancel
          </button>
          <button type="submit" disabled={creating} className={primaryButton}>
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </ScreenOverlay>
  );
}

interface Asking {
  /** The version it starts from, and how to call it, such as v5. */
  from: { versionId: string; label: string } | null;
  afterwards: (draftId: string) => void | Promise<void>;
}

const NewVersionContext = createContext<(asking: Asking) => void>(() => {});

/** Starts a new version of the preset, asking for its number first. */
export const useNewVersion = () => useContext(NewVersionContext);

/** Lets anything inside the editor start a new version through the one dialog. */
export function NewVersionProvider({
  editing,
  children,
}: {
  editing: PresetEditing;
  children: ReactNode;
}) {
  const [asking, setAsking] = useState<Asking | null>(null);
  return (
    <NewVersionContext.Provider value={setAsking}>
      {children}
      {asking && (
        <NewVersionDialog
          basedOn={asking.from?.label ?? null}
          taken={[...editing.drafts, ...editing.versions].map(known => known.versionNumber)}
          onCreate={async versionNumber => {
            const { draft } = await api.presets.drafts.create({
              params: { slug: editing.preset.slug },
              body: {
                from: asking.from ? { versionId: asking.from.versionId } : null,
                versionNumber,
              },
            });
            await asking.afterwards(draft.id);
          }}
          onClose={() => setAsking(null)}
        />
      )}
    </NewVersionContext.Provider>
  );
}
